import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faChartLine,
  faChartPie,
  faDownload,
  faCalendar,
  faFilter,
  faFileAlt,
  faUsers,
  faEuroSign,
  faClock,
  faStar,
} from '@fortawesome/free-solid-svg-icons';

const ReportsPage: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const breadcrumb = [{ label: 'Reporty' }, { label: 'Přehled reportů' }];

  const reportTypes = [
    { id: 'overview', name: 'Přehled výkonnosti', icon: faChartBar, description: 'Celkový přehled tržeb a výkonů' },
    { id: 'revenue', name: 'Tržby', icon: faEuroSign, description: 'Detailní analýza příjmů' },
    { id: 'customers', name: 'Zákazníci', icon: faUsers, description: 'Statistiky zákazníků a jejich chování' },
    { id: 'staff', name: 'Zaměstnanci', icon: faUsers, description: 'Výkonnost jednotlivých zaměstnanců' },
    { id: 'services', name: 'Služby', icon: faClock, description: 'Analýza popularity a ziskovosti služeb' },
    { id: 'financial', name: 'Finanční', icon: faChartLine, description: 'Finanční přehled a prognózy' },
  ];

  const overviewStats = [
    { title: 'Celkové tržby', value: '156,230 Kč', change: '+12%', icon: faEuroSign, color: 'text-green-600' },
    { title: 'Počet rezervací', value: '368', change: '+8%', icon: faCalendar, color: 'text-blue-600' },
    { title: 'Noví zákazníci', value: '45', change: '+15%', icon: faUsers, color: 'text-purple-600' },
    { title: 'Průměrné hodnocení', value: '4.6', change: '+2%', icon: faStar, color: 'text-yellow-600' },
  ];

  const recentReports = [
    { name: 'Měsíční přehled - listopad 2024', type: 'overview', date: '2024-11-15', size: '2.3 MB' },
    { name: 'Tržby podle služeb - Q4 2024', type: 'revenue', date: '2024-11-10', size: '1.8 MB' },
    { name: 'Analýza zákazníků - říjen 2024', type: 'customers', date: '2024-11-05', size: '3.1 MB' },
    { name: 'Výkonnost zaměstnanců - listopad', type: 'staff', date: '2024-11-01', size: '2.7 MB' },
  ];

  const renderReportContent = () => {
    switch (selectedReport) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {overviewStats.map((stat, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <FontAwesomeIcon icon={stat.icon} className={`text-xl ${stat.color}`} />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                      <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                      <p className="text-sm text-green-600">{stat.change}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tržby v čase</h3>
                <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <FontAwesomeIcon icon={faChartLine} className="w-12 h-12 mb-2" />
                    <p>Graf bude implementován</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Rozdělení služeb</h3>
                <div className="h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <FontAwesomeIcon icon={faChartPie} className="w-12 h-12 mb-2" />
                    <p>Graf bude implementován</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'revenue':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Detailní analýza tržeb</h3>
              <div className="h-96 bg-gray-100 rounded-lg flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <FontAwesomeIcon icon={faChartBar} className="w-16 h-16 mb-4" />
                  <p className="text-lg">Detailní grafy tržeb budou implementovány</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="h-96 bg-gray-100 rounded-lg flex items-center justify-center">
              <div className="text-center text-gray-500">
                <FontAwesomeIcon icon={faFileAlt} className="w-16 h-16 mb-4" />
                <p className="text-lg">Report bude implementován</p>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Přehled reportů" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Reporty</h1>
                <p className="text-gray-600 mt-1">Analytické přehledy a statistiky</p>
              </div>
              <div className="flex items-center space-x-3">
                <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="week">Tento týden</option>
                  <option value="month">Tento měsíc</option>
                  <option value="quarter">Toto čtvrtletí</option>
                  <option value="year">Tento rok</option>
                </select>
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export reportu
                </button>
              </div>
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Report Types Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Typy reportů</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-2">
                    {reportTypes.map(report => (
                      <button
                        key={report.id}
                        onClick={() => setSelectedReport(report.id)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          selectedReport === report.id ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <FontAwesomeIcon icon={report.icon} className={`w-5 h-5 ${selectedReport === report.id ? 'text-green-600' : 'text-gray-400'}`} />
                          <div>
                            <p className={`font-medium ${selectedReport === report.id ? 'text-green-900' : 'text-gray-900'}`}>{report.name}</p>
                            <p className="text-xs text-gray-500">{report.description}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recent Reports */}
              <div className="bg-white rounded-lg shadow-sm mt-6">
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Nedávné reporty</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {recentReports.map((report, index) => (
                      <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{report.name}</p>
                          <p className="text-xs text-gray-500">{new Date(report.date).toLocaleDateString('cs-CZ')}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-400">{report.size}</span>
                          <button className="text-gray-400 hover:text-gray-600">
                            <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Report Content */}
            <div className="lg:col-span-3">{renderReportContent()}</div>
          </div>

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <FontAwesomeIcon icon={faChartBar} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Vytvořit vlastní report</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                <FontAwesomeIcon icon={faDownload} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Export všech dat</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                <FontAwesomeIcon icon={faCalendar} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Naplánovat report</span>
              </button>
              <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
                <FontAwesomeIcon icon={faFilter} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Pokročilé filtry</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
